import Rpc from "@/Rpc";
import DataStream from "@/components/forms/DataStream";
import StreamingQuery from "@/components/forms/StreamingQuery";
import StreamingQueryColOrder from "@/server/com/silkplm/rpc/dto/streaming/StreamingQueryColOrder";
import StreamSelection from "@/server/com/silkplm/rpc/dto/StreamSelection";
import FormStreamingQuery from "@/server/com/silkplm/rpc/dto/streaming/FormStreamingQuery";
import {executeUntil, nextAnimationFrame} from "@/utils/Executors";
import FeatureFlag from "@/server/com/silkplm/rpc/FeatureFlag";
import {DEBUG} from "@/components/baseGrid/GridTools";


const LIST_EXTENDED_ENTITIES = "LIST EXTENDED ENTITIES";

export default class StreamingEngine {
    /**
     * @param entitiesList {EntitiesList}
     * @param dataGrid {DataGrid2}
     */
    constructor(entitiesList, dataGrid, form) {

        /** @type {EntitiesList} */
        this.entitiesList = entitiesList;

        /** @type {DataGrid2} */
        this.dataGrid = dataGrid;

        /** @type {StreamingQuery} */
        this.query = null;

        this.streaming = false;

        this.sortableCols = new Set();

        /** @type {BaseFormConfig} */
        this.form = form;

        this.chunkLoadPending = false;
        this.chainedChunks = [];
        this.chunkRequestPromises = [];
        this.requestCounter = 0;
        this.promisesForRequest = new Map();
        this.chunkInProgress = false;
    }

    selectAll() {
        this.dataStream?.selectAll();
    }

    unselectAll() {
        this.dataStream?.unselectAll();
    }

    clearSelection() {
        this.dataStream?.clearSelection();
    }

    cleanReferences() {
        this.dataStream?.cleanReferences();
    }

    async getSelectedItems() {
        return await this.dataStream.getSelectedItems();
    }

    getStreamSelection() {
        let res = new StreamSelection();
        res.allSelectedMode = this.dataStream.defaultSelectAll;
        res.queryUUID = this.currentQueryUUID();
        if (this.form.isView) {
            if (res.allSelectedMode) {
                res.excludedViewIds = this.dataStream.getExcludedFromSelectionIds();
            } else {
                res.includedViewIds = this.dataStream.getSelectedItems().map(el => el.__full_id__);
            }
        } else {
            if (res.allSelectedMode) {
                if (this.query.case === "BUDGETING_PLANNER_PM_LIST") {
                    this.dataStream.getExcludedFromSelectionWithNullIds(res, "budgetingPlannerId");
                } else {
                    res.excludedIds = this.dataStream.getExcludedFromSelectionIds();
                }
            } else {
                if (this.query.case === "BUDGETING_PLANNER_PM_LIST") {
                    res.includedUniqueIdentityForNullIds = [];
                    res.includedIds = [];
                    this.dataStream.getSelectedItems().map(el => {
                        if (!el.id) {
                            res.includedUniqueIdentityForNullIds.push(el.budgetingPlannerId);
                        } else {
                            res.includedIds.push(el.id);
                        }
                    });
                } else {
                    res.includedIds = this.dataStream.getSelectedItems().map(el => el.id);
                }
            }
        }

        return res;
    }


    async filterByManyTexts(textToCols) {
        this.query.windowSize = StreamingQuery.DEFAULT_WINDOWS_SIZE;
        const baseGrid = this.dataGrid.$refs?.baseGrid;
        // if(baseGrid) {
        //     if (this.query.windowSize > 1.5 * baseGrid.windowSize()) {
        //         this.query.windowSize = baseGrid.windowSize() + 6;
        //     }
        // }
        this.query.offset = 0;
        this.query.complexTextQuery = {};
        for(let colFilter of textToCols){
            let colTextQuery = colFilter['text'];
            let col = colFilter['col'];
            this.query.complexTextQuery[col.id] = colTextQuery;
        }

        // console.log("Multicol filter");
        // console.log(this.query.complexTextQuery);

        this.dataStream.reset();
        if (this.isV3()) {
            this.getBaseGridV3().selectedIndex = -1;
            this.getBaseGridV3().refreshView();
        }
        //this.dataStream.initialStreamResult = {totals: 1};
        this.dataGrid.refreshStream(this.dataStream);
        if (!this.query.uuid) {
            console.info("Empty uuid");
            return
        }
        let results = await Rpc.FormsStreams.followStream(this.query.toFormStreamingQuery());
        if (results.cancelled) {
            return;
        }
        if (window && window['_logStream']) {
            console.table(results.records ?? []);
        }
        this.totals = results.totals;

        this.dataStream = new DataStream(results, this, this.dataStream)
        this.dataGrid.startStream(this, this.dataStream);
    }
    async filterByText(textQuery, col = null) {
        this.query.windowSize = StreamingQuery.DEFAULT_WINDOWS_SIZE;
        const baseGrid = this.dataGrid.$refs?.baseGrid;
        // if(baseGrid) {
        //     if (this.query.windowSize > 1.5 * baseGrid.windowSize()) {
        //         this.query.windowSize = baseGrid.windowSize() + 6;
        //     }
        // }
        this.query.offset = 0;
        this.query.textQuery = textQuery;
        this.query.activeColumns = [];
        for (let col of this.dataGrid.renderedColumns) {
            if (col.visible) {
                this.query.activeColumns.push(col.prop);
            }
        }

        if(col) {
            this.query.activeColumns = this.query.activeColumns.filter(element=> element === col.id);
        }

        this.dataStream.reset();
        if (this.isV3()) {
            this.getBaseGridV3().selectedIndex = -1;
            this.getBaseGridV3().refreshView();
        }
        //this.dataStream.initialStreamResult = {totals: 1};
        this.dataGrid.refreshStream(this.dataStream);
        if (!this.query.uuid) {
            console.info("Empty uuid");
            return
        }
        let results = await Rpc.FormsStreams.followStream(this.query.toFormStreamingQuery());
        if (results.cancelled) {
            return;
        }
        if (window && window['_logStream']) {
            console.table(results.records ?? []);
        }
        this.totals = results.totals;

        this.dataStream = new DataStream(results, this, this.dataStream)
        this.dataGrid.startStream(this, this.dataStream);
    }

    /**
     * @param query {StreamingQuery}
     */
    async executeQuery(query) {
        if (!this.dataGrid) {
            // console.log("X3");
            this.entitiesList?.setLoadInProgress(false);
            return;
        }
        let resetView = false;
        resetView = !this.query ||
            query.forceViewReset ||
            query.viewSignature() !== this.query.viewSignature();

        if (this.query) {
            query.order = this.query.order;
        }

        if (!resetView) {
            if (this.isV3()) {
                if (this.getBaseGridV3()) {
                    query.offset = this.getBaseGridV3().findMinRowIndex();
                }
            }
        }
        if (this.isV3()) {
            if (this.dataGrid?.gridHeader?.showSearch && this.dataGrid.gridHeader.lastQuery) {
                query.textQuery = this.dataGrid.gridHeader.lastQuery;
            }
        }
        this.query = query;
        this.query.forceViewReset = false;

        this.streaming = true;
        if (this.entitiesList) {
            this.entitiesList.streaming = true;
        }
        this.dataGrid.streaming = true;

        this.entitiesList.pendinQuery = this;
        this.dataGrid.pendinQuery = this;

        let renderedColumns = this.dataGrid.renderedColumns;
        let sortableColumns = [];
        renderedColumns.forEach(col => {
            if (col.order) {
                sortableColumns.push(col);
            }
        });
        let sortableCol = null;
        if (sortableColumns?.length) {
            sortableCol = sortableColumns[sortableColumns.length - 1];
        }
        if (sortableCol) {
            let order = sortableCol.order;
            let ord = new StreamingQueryColOrder();
            ord.ascend = order === 'asc';
            ord.colName = sortableCol.prop;
            this.query.order = [
                ord
            ];
        }

        let results = await Rpc.FormsStreams.startStream(this.query.toFormStreamingQuery()).lock();
        if (window && window['_logStream']) {
            console.table(results.records ?? []);
        }

        this.sortableCols = new Set();
        for (let col of results.sortableColumns) {
            this.sortableCols.add(col);
        }
        this.query.uuid = results.streamUuid;
        this.totals = results.totals;

        this.dataStream = new DataStream(results, this, this.dataStream)

        this.dataGrid.startStream(this, this.dataStream);
        if (this.isV3()) {
            if (resetView) {
                if (this.getBaseGridV3()) {
                    this.getBaseGridV3().setVScrollPos(0);
                }
            }
        }
        this.dataGrid.$nextTick(() => {
            // console.log("X4");
            this.entitiesList?.setLoadInProgress(false);
        })
    }

    currentQueryUUID() {
        return this.dataStream.initialStreamResult.streamUuid;
    }

    setFilteringMode(mode) {
        this.query.toFormStreamingQuery().filteringMode = mode;
    }
    stopStreaming() {
        this.streaming = false;
        this.entitiesList.streaming = false;
        if (this.dataGrid?.streaming) {
            this.dataGrid.streaming = false;
        }

        this.entitiesList.pendinQuery = null;
        if (this.dataGrid) {
            this.dataGrid.pendinQuery = null;
        }
    }

    async requestChunk(offset, windowSize) {
        // console.log("request chunk start");
        // console.log("request chunk offset: ", offset);
        // console.log("request chunk window size: ", windowSize);
        if(windowSize<=0){
            console.error("Negative or zer window!");
            windowSize = 0;
        }
        return new Promise(resolve => {
            this.chunkRequestPromises.push(resolve);
            this.chainedChunks.push({
                offset,
                windowSize
            });
            this.scheduleChunkLoad();
        });
    }

    async scheduleChunkLoad() {
        if (this.chunkLoadPending) {
            return;
        }
        this.chunkLoadPending = true;

        this.dataGrid.$nextTick(() => {
            this.chunkLoadPending = false;
            DEBUG && console.log(`Reload checkpoint #1; focus: ${this.getBaseGridV3().focusOnIndex}`);

            let minIndex = -1;
            let maxIndex = -1;

            for (let chunk of this.chainedChunks) {
                if (minIndex < 0) {
                    minIndex = chunk.offset;
                    maxIndex = chunk.offset + chunk.windowSize - 1;
                } else {
                    if (chunk.offset < minIndex) {
                        minIndex = chunk.offset;
                    }
                    if (chunk.offset + chunk.windowSize - 1 > maxIndex) {
                        maxIndex = chunk.offset + chunk.windowSize - 1;
                    }
                }
            }
            DEBUG && console.log(`Cumulating chunks into one: ${this.chainedChunks.length}`);

            if (this.chunkInProgress) {
                this.promisesForRequest.forEach((value, key) => {
                    this.chunkRequestPromises = [...this.chunkRequestPromises, ...value];
                });
                this.promisesForRequest = new Map();
                let oldOffset = this.query.offset;
                if (this.query.offset < minIndex) {
                    minIndex = this.query.offset;
                }
                if (this.query.windowSize + oldOffset > maxIndex) {
                    maxIndex = this.query.windowSize + oldOffset;
                }
                DEBUG && console.log(`Previous chunk in progress, merging scopes: ${minIndex} - ${maxIndex}`);
            }

            if (this.isV3()) {
                let realMinIndex = this.getBaseGridV3().findMinRowIndex();
                let realMaxIndex = realMinIndex + StreamingQuery.DEFAULT_WINDOWS_SIZE;
                if (minIndex < realMinIndex) {
                    minIndex = realMinIndex;
                }
                if (maxIndex > realMaxIndex) {
                    maxIndex = realMaxIndex;
                }
            }

            let offset = minIndex;
            let windowSize = maxIndex - minIndex + 1;
            if(windowSize<=0){
                windowSize = 0;
            }
            DEBUG && console.log(`Effective offset: ${offset} window: ${windowSize}`);

            this.requestChunkExecute(offset, windowSize, this.chunkRequestPromises).then();
            this.chunkRequestPromises = [];
            this.chainedChunks = [];
        });
    }

    // nowa wersja
    async requestChunkExecute(offset, windowSize, chunkRequestPromises) {
        let results = null;
        let currentExecution = this.requestCounter++;
        this.promisesForRequest.set(currentExecution, chunkRequestPromises);
        try {
            if (windowSize <= 0) {
                this.chunkInProgress = false;
                chunkRequestPromises.forEach(r => {
                    try {
                        r()
                    } catch (e) {
                        console.log((e))
                    }
                });
                // console.log("return 1");
                return;
            }

            this.query.offset = offset;
            this.query.windowSize = windowSize;
            if (!this.query.uuid) {
                // console.log("return 2 - no uuid");
                return
            }
            this.chunkInProgress = true;

            // console.log("offset : ", this.query.offset)
            // console.log("window size: ", this.query.windowSize)
            // console.log("current execution: ", currentExecution);
            results = await Rpc.FormsStreams.followStream(this.query.toFormStreamingQuery());
            if (results.cancelled) {
                // console.log("canceled current execution: ", currentExecution);
                return;
            }
            if (window && window['_logStream']) {
                console.table(results.records ?? []);
            }
            // console.log("current execution: ", currentExecution);
            // console.log("size of records: ", results.records.length);
            await this.dataStream.processChunk(results);
            if (SessionManager.hasFlag(FeatureFlag.GRID_V3)) {
                DEBUG && console.log(`Reload checkpoint #2; focus: ${this.getBaseGridV3().focusOnIndex}`);
                if (this.dataGrid.$refs.baseGrid) {
                    this.dataGrid.$refs.baseGrid.validateTotalSize();
                    this.dataGrid.$refs.baseGrid.onChunkLoaded(results.offset, results.records.length);
                }
            } else {
                this.dataGrid.refreshStream(this.dataStream);
            }
        } finally {
            this.chunkInProgress = false;
            if (this.promisesForRequest.has(currentExecution)) {
                if (currentExecution === 1) {
                    // console.log("curren execution is 1");
                }
                let promises = this.promisesForRequest.get(currentExecution);
                // console.log("execution promises for: ", currentExecution);
                promises.forEach(r => {
                    try {
                        r()
                    } catch (e) {
                        console.log((e))
                    }
                });
                this.promisesForRequest.delete(currentExecution);
            }
        }
    }


    // stara wersja
    async requestChunkExecute1(offset, windowSize, chunkRequestPromises) {
        try {
            if (windowSize <= 0) {
                this.chunkInProgress = false;
                chunkRequestPromises.forEach(r => {
                    try {
                        r()
                    } catch (e) {
                        console.log((e))
                    }
                });
                return;
            }

            this.query.offset = offset;
            this.query.windowSize = windowSize;
            if (!this.query.uuid) {
                return
            }
            this.chunkInProgress = true;

            let results = await Rpc.FormsStreams.followStream(this.query.toFormStreamingQuery());
            if (results.cancelled) {
                return;
            }
            if (window && window['_logStream']) {
                console.table(results.records ?? []);
            }
            await this.dataStream.processChunk(results);
            if (SessionManager.hasFlag(FeatureFlag.GRID_V3)) {
                DEBUG && console.log(`Reload checkpoint #2; focus: ${this.getBaseGridV3().focusOnIndex}`);
                if (this.dataGrid.$refs.baseGrid) {
                    this.dataGrid.$refs.baseGrid.validateTotalSize();
                    this.dataGrid.$refs.baseGrid.onChunkLoaded(results.offset, results.records.length);
                }
            } else {
                this.dataGrid.refreshStream(this.dataStream);
            }
        } finally {
            this.chunkInProgress = false;
            chunkRequestPromises.forEach(r => {
                try {
                    r()
                } catch (e) {
                    console.log((e))
                }
            });
        }
    }

    /**
     *
     * @return {BaseGrid}
     */
    getBaseGridV3() {
        if (this.dataGrid.v3) {
            return this.dataGrid.$refs.baseGrid;
        }
        return null;
    }

    isV3() {
        return this.dataGrid?.v3;
    }

    findOffset() {

        let min = -1;
        if (this.dataGrid.v3) {
            min = this.dataGrid.$refs.baseGrid.findMinRowIndex();
        } else {
            let rows = this.dataGrid.$el.querySelectorAll(("[data-row-id]>div"));
            for (let r of rows) {
                let rowIndex = parseInt(r.getAttribute('data-row'));
                if (min === -1 || rowIndex < min) {
                    min = rowIndex;
                }
            }
        }

        min -= 12;
        if (min < 0) {
            min = 0;
        }

        return min;
    }

    async sortBy(column) {
        if (!this.sortableCols.has(column.prop)) {
            return "";
        }
        let order;
        if (SessionManager.hasFlag(FeatureFlag.GRID_V3)) {
            order = column.order && column.order === 'asc' ? 'asc' : 'desc';
        } else {
            order = column.order && column.order === 'asc' ? 'desc' : 'asc';
        }

        this.query.windowSize = StreamingQuery.DEFAULT_WINDOWS_SIZE;
        this.query.offset = this.findOffset();
        if (this.dataGrid.localSelectedItem) {
            if (this.dataGrid.localSelectedItem.__full_id__) {
                this.query.viewLocateId = this.dataGrid.localSelectedItem.__full_id__;
                this.query.locateId = null;
            } else {
                this.query.viewLocateId = null;
                this.query.locateId = this.dataGrid.localSelectedItem.id;
            }
        }
        let ord = new StreamingQueryColOrder();
        ord.ascend = order === 'asc';
        ord.colName = column.prop;
        this.query.order = [
            ord
        ];

        this.dataStream.reset();
        this.dataGrid.refreshStream(this.dataStream);
        if (!this.query.uuid) {
            console.info("Empty uuid");
            return
        }
        let results = await Rpc.FormsStreams.followStream(this.query.toFormStreamingQuery());
        if (results.cancelled) {
            return;
        }
        if (window && window['_logStream']) {
            console.table(results.records ?? []);
        }
        await this.dataStream.reorder(results);
        this.dataGrid.refreshStream(this.dataStream);
        this.query.locateId = null;
        this.query.viewLocateId = null;
        return order;
    }

    async onDelete(changeEvent) {
        if (!this.dataGrid.v3) {
            await this.reload();
            return;
        }

        if (!this.dataStream.delete(changeEvent.id)) {
            await this.reload();
        } else {
            if (this.dataGrid.$refs.baseGrid) {
                this.dataGrid.$refs.baseGrid.focusOnIndex = -1;
            }
            this.dataGrid.$refs.baseGrid?.refreshView();
        }
    }

    async onInsert(changeEvent, item) {
        if (!this.dataGrid.v3) {
            await this.reload();
            return;
        }

        if (!this.query.uuid) {
            console.info("Empty uuid");
            return
        }
        if (item?.__full_id__) {
            this.query.viewLocateId = item?.__full_id__;
            this.query.locateId = null;
        } else {
            this.query.viewLocateId = null;
            this.query.locateId = item?.id;
        }

        let results = await Rpc.FormsStreams.followStream(this.query.toFormStreamingQuery());
        this.query.locateId = null;
        this.query.viewLocateId = null;
        if (!results.records) {
            return;
        }
        if (results.records.length === 0) {
            return;
        }
        if (!this.dataStream.add(results)) {
            await this.reload();
        } else {
            if (this.query.isDetail) {
                await this.reload();
            } else {
                this.dataGrid.$refs.baseGrid?.focusOnOffset(results.locatedOffset);
                this.dataGrid.$refs.baseGrid?.refreshView();
            }
        }
    }

    async onBulkChange(changeEvent) {
        await this.reload();
    }

    async onUpdate(changeEvent) {
        // edycja moze zmienic kolejnosc sortowania

        if (!this.query.refreshComparator) {
            let offset = this.dataStream.findItemById(changeEvent.instance);
            if (offset != null) {
                this.dataStream.invalidateItemAtOffset(offset);
                this.dataGrid.refreshStream(this.dataStream);
                await this.requestChunk(offset, 1);
                if (this.dataStream.loadedRows.get(offset)?.id !== changeEvent.instance.id) {
                    await this.reload();
                } else {
                    this.dataGrid.refreshStream(this.dataStream);
                }
            }
        } else {
            let offsets = this.dataStream.findItems(changeEvent.instance, this.query.refreshComparator);
            if (offsets.length) {
                for (let offset of offsets) {
                    this.dataStream.invalidateItemAtOffset(offset);
                }
                for (let offset of offsets) {
                    this.requestChunk(offset, 1).then();
                }
                this.dataGrid.refreshStream(this.dataStream);
            }

        }
    }

    async reload(keepScrollPosition = true) {
        setTimeout(async () => {

            this.dataStream.reset();
            this.query.windowSize = StreamingQuery.DEFAULT_WINDOWS_SIZE;
            if (keepScrollPosition && this.dataGrid.$refs.baseGrid && this.dataGrid.$refs.baseGrid.findMinRowIndex()) {
                this.query.offset = this.dataGrid.$refs.baseGrid.findMinRowIndex();
            }
            //this.query.offset = this.findOffset();

            this.dataGrid.refreshStream(this.dataStream);
            if (!this.query.uuid) {
                // console.info("Empty uuid");
                return
            }
            let results = await Rpc.FormsStreams.followStream(this.query.toFormStreamingQuery());
            this.query.locateId = null;
            this.query.viewLocateId = null;
            if (results.cancelled) {
                return;
            }
            if (window && window['_logStream']) {
                console.table(results.records ?? []);
            }
            await this.dataStream.processChunk(results);
            Log.debug(`Reloading ${this.query.windowSize}/${this.query.offset}`);
            //console.log(`Refreshing ${results.offset} ${results.records.length}`);
            this.dataGrid.refreshStream(this.dataStream);
            // if(results.locatedOffset >=0) {
            //     await nextAnimationFrame(() => {
            //         this.focusOnIndex(results.locatedOffset);
            //     });
            // }
        });
    }

    async materialize(format) {
        return Rpc.FormsStreams.materialize(this.getStreamSelection(), format);
    }

    focusOnIndex(index, scroll = true) {
        if (!this.dataGrid) {
            return;
        }
        if (index === undefined || index == null) {
            this.dataGrid.gridComponent.clearFocus();
            return;
        }

        if (scroll) {
            if (index < 0) {
                index = 0;
            }
            this.dataGrid.gridComponent?.scrollToRow(0).then(() => {

                this.dataGrid.gridComponent?.scrollToRow(index).then(() =>
                    executeUntil(() => {
                        let div = this.dataGrid.gridData.querySelector(`div[data-row='${index}']`);
                        if (div) {
                            window.emulateClick(div.parentElement);
                            return true;
                        }
                        return false;
                    }, 5).then());
            });
        } else {
            executeUntil(() => {
                let div = this.dataGrid.gridData.querySelector(`div[data-row='${index}']`);
                if (div) {
                    window.emulateClick(div.parentElement);
                    return true;
                }
                return false;
            }, 5).then();

        }

    }
}